import { $getNodeByKey } from "lexical";
import { isEmpty } from "lodash-es";
import { getAspectRatio } from "../../../../utils/aspect-ratio";
import { $createCustomEmojiNodeMui } from "../nodes/custom-emoji/custom-emoji-node-mui";
import { $createDesignHuddleNodeFromAsset } from "../nodes/design-huddle-mui";
import { $isImageNode, type ImageNode } from "../nodes/image-node";
import { $createImageNode } from "../nodes/image-node-mui";
import { $loadDesignInto, type DesignNodeFactories } from "./load-design-into";

import type { SerialisedEditorDesign } from "../types/serialised-editor-design";
import type { LexicalEditor } from "lexical";

// The aspect ratio is only known once the browser has loaded the image, so insert at the fallback
// ratio and resize in a later update.
const $createSizedImageNode = (
  editor: LexicalEditor,
  url: string,
  proportionOfMaxWidth: number,
  aspect: number,
): ImageNode => {
  const imageNode = $createImageNode(editor._headless, url, proportionOfMaxWidth, aspect);
  const nodeKey = imageNode.getKey();
  void getAspectRatio(url)
    .then((loadedAspect) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isImageNode(node) && node.__url === url) {
          node.setSize(node.__proportionOfMaxWidth, loadedAspect);
        }
      });
    })
    .catch(() => undefined);
  return imageNode;
};

const muiDesignNodes = (editor: LexicalEditor): DesignNodeFactories => ({
  designHuddle: (account_id, project_id, preview_url, proportionOfMaxWidth, aspect) =>
    $createDesignHuddleNodeFromAsset(
      editor._headless,
      account_id,
      project_id,
      proportionOfMaxWidth,
      aspect,
      undefined,
      preview_url,
    ),
  image: (url, proportionOfMaxWidth, aspect) =>
    $createSizedImageNode(editor, url, proportionOfMaxWidth, aspect),
  customEmoji: (name) => $createCustomEmojiNodeMui(name),
});

// Undefined for an empty design, so the editor keeps whatever initial state it would have had.
export const loadDesign = async (
  design: SerialisedEditorDesign | undefined,
  format: "email" | "chat" | "sms",
): Promise<((e: LexicalEditor) => void) | undefined> =>
  isEmpty(design) ? undefined : (e) => $loadDesignInto(e, design, format, muiDesignNodes(e));
